import React from 'react';
import { Box, Typography, Paper, Divider, Stack, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useLocation, useNavigate } from 'react-router-dom';
import SideBar from '../Dashboard/StudentDashboard/Sidebar/SideBar';

const QuizResults = ({
    questions,
    userAnswers,
    apiResponse,
    onReturn
}) => {
    // Calculate total score and create correct answers map
    const correctAnswersMap = {};
    let totalScore = 0;
    let totalPossibleScore = 0;

    apiResponse.quizResponseList.forEach(item => {
        correctAnswersMap[item.quizQuestionId] = item.quizCorrectAnswer;
        totalScore += item.quizAnswerPoint;
        totalPossibleScore += 20; // Assuming each question is worth 20 points
    });

    // Find question text by ID
    const getQuestionText = (questionId) => {
        const question = questions.find(q => q.quizQuestionId === questionId);
        return question ? question.quizQuestion : `Question ID: ${questionId}`;
    };

    // Find user's selected option text
    const getUserAnswerText = (questionId, userAnswer) => {
        const question = questions.find(q => q.quizQuestionId === questionId);
        if (!question || !userAnswer) return "Not answered";

        const options = question.quizOptions[0];
        for (let i = 1; i <= 4; i++) {
            if (options[`quizOption${i}`] === userAnswer) {
                return userAnswer;
            }
        }
        return userAnswer; // Return raw answer if not found in options
    };

    return (
        <Paper elevation={3} sx={{ p: 3, mb: 3, maxWidth: 800, margin: '0 auto' }}>
            <Typography variant="h4" gutterBottom align="center">Quiz Results</Typography>
            <Typography variant="h5" gutterBottom align="center">
                Score: {totalScore}/{totalPossibleScore} points
            </Typography>
            <Typography variant="subtitle1" align="center" gutterBottom>
                ({Math.round((totalScore / totalPossibleScore) * 100)}%)
            </Typography>

            <Divider sx={{ my: 3 }} />

            {apiResponse.quizResponseList.map((item, index) => {
                const questionText = getQuestionText(item.quizQuestionId);
                const userAnswer = getUserAnswerText(item.quizQuestionId, userAnswers[item.quizQuestionId]);
                const isCorrect = item.quizAnswerPoint > 0;

                return (
                    <Box key={`${item.quizQuestionId}-${index}`} sx={{ mb: 3 }}>
                        <Stack direction="row" alignItems="flex-start" spacing={1}>
                            {isCorrect ? (
                                <CheckCircleIcon color="success" sx={{ mt: 0.5 }} />
                            ) : (
                                <CancelIcon color="error" sx={{ mt: 0.5 }} />
                            )}
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="h6" component="div">
                                    Question {index + 1}: {questionText}
                                </Typography>

                                <Box sx={{ ml: 2, mt: 1 }}>
                                    <Typography>
                                        <strong>Your answer:</strong> <span style={{ color: isCorrect ? '#4caf50' : '#f44336' }}>
                                            {userAnswer}
                                        </span>
                                    </Typography>
                                    {!isCorrect && (
                                        <Typography>
                                            <strong>Correct answer:</strong> {item.quizCorrectAnswer}
                                        </Typography>
                                    )}
                                    <Typography>
                                        <strong>Points:</strong> <span style={{ color: isCorrect ? '#4caf50' : '#f44336' }}>
                                            {item.quizAnswerPoint}/20
                                        </span>
                                    </Typography>
                                </Box>
                            </Box>
                        </Stack>

                        <Divider sx={{ mt: 2 }} />
                    </Box>
                );
            })}

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Button
                    variant="contained"
                    onClick={onReturn}
                    size="large"
                    sx={{ px: 4 }}
                >
                    Return to Dashboard
                </Button>
            </Box>
        </Paper>
    );
};

const QuizCompletionPage = () => {
    const location = useLocation();
    const navigate = useNavigate();

    // Safely access state with default values
    const {
        questions = [],
        userAnswers = {},
        apiResponse = null
    } = location.state || {};

    // Error states
    if (!location.state) {
        return (
            <Box sx={{ p: 3, textAlign: 'center', mt: 4 }}>
                <Typography variant="h5" color="error" gutterBottom>
                    Missing quiz results data
                </Typography>
                <Typography sx={{ mb: 3 }}>
                    Please complete the quiz to view results
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => navigate('/StudentDashboard')}
                >
                    Return to Dashboard
                </Button>
            </Box>
        );
    }

    if (!apiResponse || apiResponse.response !== "SUCCESS") {
        return (
            <Box sx={{ p: 3, textAlign: 'center', mt: 4 }}>
                <Typography variant="h5" color="error" gutterBottom>
                    Invalid quiz results
                </Typography>
                <Typography sx={{ mb: 3 }}>
                    There was a problem loading your quiz results
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => navigate('/StudentDashboard')}
                >
                    Return to Dashboard
                </Button>
            </Box>
        );
    }

    return (
        <>
        <SideBar />
        <Box sx={{ p: 3 }}>
            <QuizResults
                questions={questions}
                userAnswers={userAnswers}
                apiResponse={apiResponse}
                onReturn={() => navigate('/StudentDashboard')}
            />
        </Box>
        </>
    );
};

export default QuizCompletionPage;